import { useRef, useEffect, useState } from 'react';
import { gsap } from 'gsap';
import {
  SlidersHorizontal,
  Calendar,
  ArrowLeft,
  LoaderCircle,
  CheckCircle2,
  AlertTriangle,
  ExternalLink,
} from 'lucide-react';
import type { DirectAnswer, LegacyQuoteCard } from '@/types';
import { QuoteCardItem } from '@/components/quote/QuoteCardItem';

interface ResultsViewProps {
  isOpen: boolean;
  onClose: () => void;
  query: string;
  results: LegacyQuoteCard[];
  sources: string[];
  answer: DirectAnswer | null;
  isSearching: boolean;
  error: string | null;
  onViewDetail: (card: LegacyQuoteCard) => void;
  onAddToTrail: (card: LegacyQuoteCard) => void;
  onShare: (card: LegacyQuoteCard) => void;
  savedCardIds: string[];
}

export function ResultsView({
  isOpen,
  onClose,
  query,
  results,
  sources,
  answer,
  isSearching,
  error,
  onViewDetail,
  onAddToTrail,
  onShare,
  savedCardIds,
}: ResultsViewProps) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [filterMode, setFilterMode] = useState<'all' | 'A' | 'B' | 'C'>('all');
  const [viewMode, setViewMode] = useState<'stack' | 'timeline' | 'argument'>('stack');

  useEffect(() => {
    if (isOpen) {
      gsap.fromTo(
        overlayRef.current,
        { opacity: 0 },
        { opacity: 1, duration: 0.3, ease: 'power2.out' }
      );
      gsap.fromTo(contentRef.current, { x: '100%' }, { x: 0, duration: 0.4, ease: 'power2.out' });
    }
  }, [isOpen]);

  const handleClose = () => {
    gsap.to(contentRef.current, {
      x: '100%',
      duration: 0.3,
      ease: 'power2.in',
    });
    gsap.to(overlayRef.current, {
      opacity: 0,
      duration: 0.3,
      delay: 0.1,
      ease: 'power2.in',
      onComplete: onClose,
    });
  };

  const filteredResults =
    filterMode === 'all' ? results : results.filter((card) => card.lane === filterMode);
  const sourceLabels: Record<string, string> = {
    federal_register: 'Federal Register',
    congress: 'Congress.gov',
    fred: 'FRED',
    official: 'Official agency record',
    mock: 'Demo data',
  };
  const displayedSources = sources.map((source) => sourceLabels[source] ?? source);
  const supportsResults = filteredResults.filter((card) => card.stance === 'supports');
  const contradictsResults = filteredResults.filter((card) => card.stance === 'contradicts');
  const neutralResults = filteredResults.filter(
    (card) => card.stance !== 'supports' && card.stance !== 'contradicts'
  );

  const getLaneColor = (lane: string) => {
    switch (lane) {
      case 'A':
        return 'bg-blue-500';
      case 'B':
        return 'bg-amber-500';
      case 'C':
        return 'bg-green-500';
      default:
        return 'bg-gray-500';
    }
  };

  if (!isOpen) return null;

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-[200] bg-[#0B0D10]/50 backdrop-blur-sm"
      onClick={(e) => e.target === overlayRef.current && handleClose()}
    >
      <div
        ref={contentRef}
        className="absolute bottom-0 right-0 top-0 flex w-full max-w-3xl flex-col overflow-hidden bg-[#F6F5F2] shadow-2xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-[rgba(11,13,16,0.08)] bg-white p-6">
          <div className="flex items-center gap-4">
            <button
              onClick={handleClose}
              className="rounded-lg p-2 transition-colors hover:bg-[#F6F5F2]"
              aria-label="Close search results"
            >
              <ArrowLeft className="h-5 w-5 text-[#6B6F78]" />
            </button>
            <div>
              <h2 className="font-display text-xl font-bold text-[#0B0D10]">Answer & evidence</h2>
              <p className="text-sm text-[#6B6F78]">"{query}"</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* View Mode Toggle */}
            <div className="flex rounded-lg bg-[#F6F5F2] p-1">
              <button
                onClick={() => setViewMode('stack')}
                aria-label="Stack view"
                className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                  viewMode === 'stack' ? 'bg-white text-[#0B0D10] shadow-sm' : 'text-[#6B6F78]'
                }`}
              >
                Stack
              </button>
              <button
                onClick={() => setViewMode('timeline')}
                aria-label="Timeline view"
                className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                  viewMode === 'timeline' ? 'bg-white text-[#0B0D10] shadow-sm' : 'text-[#6B6F78]'
                }`}
              >
                <Calendar className="h-4 w-4" />
              </button>
              <button
                onClick={() => setViewMode('argument')}
                aria-label="Group by stance"
                className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                  viewMode === 'argument' ? 'bg-white text-[#0B0D10] shadow-sm' : 'text-[#6B6F78]'
                }`}
              >
                <SlidersHorizontal className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        {!isSearching && !error && answer && (
          <section
            aria-labelledby="direct-answer-heading"
            className={`border-b px-6 py-6 ${
              answer.status === 'verified'
                ? 'border-emerald-200 bg-emerald-50/70'
                : 'border-amber-200 bg-amber-50/70'
            }`}
          >
            <div className="flex items-start gap-3">
              {answer.status === 'verified' ? (
                <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-emerald-700" />
              ) : (
                <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-700" />
              )}
              <div className="min-w-0">
                <p
                  id="direct-answer-heading"
                  className={`font-mono text-xs font-semibold uppercase tracking-[0.1em] ${
                    answer.status === 'verified' ? 'text-emerald-800' : 'text-amber-800'
                  }`}
                >
                  {answer.status === 'verified' ? 'Direct answer' : 'Answer status'}
                </p>
                <p className="font-display mt-2 text-3xl font-bold leading-tight text-[#0B0D10]">
                  {answer.shortAnswer}
                </p>
                <p className="mt-2 max-w-2xl text-base leading-relaxed text-[#3F444D]">
                  {answer.text}
                </p>
                <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
                  <span
                    className={`font-medium ${
                      answer.confidence === 'high' ? 'text-emerald-800' : 'text-amber-800'
                    }`}
                  >
                    {answer.confidence === 'high'
                      ? 'High confidence — directly supported'
                      : answer.confidence === 'medium'
                        ? 'Medium confidence — review the evidence'
                        : 'Low confidence — not directly verified'}
                  </span>
                  {answer.sourceUrl && answer.sourceName && (
                    <a
                      href={answer.sourceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 font-medium text-[#E1062C] hover:underline"
                    >
                      {answer.sourceName}
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  )}
                </div>
                {answer.note && <p className="mt-3 text-sm text-[#6B6F78]">{answer.note}</p>}
              </div>
            </div>
          </section>
        )}

        {/* Evidence Lane Filters */}
        <div className="border-b border-[rgba(11,13,16,0.08)] px-6 py-4">
          <p className="mb-3 font-mono text-xs font-semibold uppercase tracking-[0.08em] text-[#6B6F78]">
            Supporting evidence
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setFilterMode('all')}
              className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                filterMode === 'all'
                  ? 'bg-[#0B0D10] text-white'
                  : 'border border-[rgba(11,13,16,0.08)] bg-white text-[#6B6F78] hover:text-[#0B0D10]'
              }`}
            >
              All lanes
            </button>
            {['A', 'B', 'C'].map((lane) => (
              <button
                key={lane}
                onClick={() => setFilterMode(lane as 'A' | 'B' | 'C')}
                className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                  filterMode === lane
                    ? 'bg-[#0B0D10] text-white'
                    : 'border border-[rgba(11,13,16,0.08)] bg-white text-[#6B6F78] hover:text-[#0B0D10]'
                }`}
              >
                <div className={`h-2 w-2 rounded-full ${getLaneColor(lane)}`} />
                Lane {lane}
              </button>
            ))}
          </div>
        </div>

        {/* Results */}
        <div className="flex-1 overflow-y-auto p-6">
          {isSearching ? (
            <div className="flex h-full flex-col items-center justify-center text-center">
              <LoaderCircle className="mb-4 h-8 w-8 animate-spin text-[#E1062C]" />
              <p className="text-[#6B6F78]" role="status">
                Searching primary sources…
              </p>
            </div>
          ) : error ? (
            <div className="flex h-full flex-col items-center justify-center text-center">
              <p className="font-medium text-red-600" role="alert">
                {error}
              </p>
              <p className="mt-1 text-sm text-[#6B6F78]">Close this panel and try again.</p>
            </div>
          ) : filteredResults.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center text-center">
              <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-[#F6F5F2]">
                <SlidersHorizontal className="h-8 w-8 text-[#6B6F78]" />
              </div>
              <p className="text-[#6B6F78]">No results found</p>
              <p className="mt-1 text-sm text-[#9CA3AF]">Try a different search term</p>
            </div>
          ) : (
            <div className="space-y-4">
              {viewMode === 'stack' && (
                <div className="relative">
                  {filteredResults.map((card, index) => (
                    <div
                      key={card.id}
                      className="mb-4"
                      style={{
                        transform: index > 0 ? `translateY(-${index * 8}px)` : undefined,
                        zIndex: filteredResults.length - index,
                      }}
                    >
                      <QuoteCardItem
                        card={card}
                        isSaved={savedCardIds.includes(card.id)}
                        onViewDetail={() => onViewDetail(card)}
                        onAddToTrail={() => onAddToTrail(card)}
                        onShare={() => onShare(card)}
                      />
                    </div>
                  ))}
                </div>
              )}

              {viewMode === 'timeline' && (
                <div className="relative border-l-2 border-[rgba(11,13,16,0.1)] pl-8">
                  {[...filteredResults]
                    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
                    .map((card) => (
                      <div key={card.id} className="relative mb-6">
                        <div className="absolute -left-[41px] h-5 w-5 rounded-full border-2 border-[#E1062C] bg-white" />
                        <div className="mb-2 font-mono text-sm text-[#6B6F78]">{card.date}</div>
                        <QuoteCardItem
                          card={card}
                          isSaved={savedCardIds.includes(card.id)}
                          onViewDetail={() => onViewDetail(card)}
                          onAddToTrail={() => onAddToTrail(card)}
                          onShare={() => onShare(card)}
                          compact
                        />
                      </div>
                    ))}
                </div>
              )}

              {viewMode === 'argument' && (
                <div className="space-y-6">
                  {[
                    { title: 'Supports claim', color: 'text-green-600', cards: supportsResults },
                    {
                      title: 'Contradicts claim',
                      color: 'text-red-600',
                      cards: contradictsResults,
                    },
                    {
                      title: 'Neutral or unclear',
                      color: 'text-[#6B6F78]',
                      cards: neutralResults,
                    },
                  ]
                    .filter((group) => group.cards.length > 0)
                    .map((group) => (
                      <div key={group.title}>
                        <h3
                          className={`mb-3 font-mono text-xs uppercase tracking-[0.08em] ${group.color}`}
                        >
                          {group.title}
                        </h3>
                        {group.cards.map((card) => (
                          <div key={card.id} className="mb-3">
                            <QuoteCardItem
                              card={card}
                              isSaved={savedCardIds.includes(card.id)}
                              onViewDetail={() => onViewDetail(card)}
                              onAddToTrail={() => onAddToTrail(card)}
                              onShare={() => onShare(card)}
                              compact
                            />
                          </div>
                        ))}
                      </div>
                    ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-[rgba(11,13,16,0.08)] bg-white p-4">
          <div className="flex items-center justify-between text-sm text-[#6B6F78]">
            <span>
              {isSearching
                ? 'Searching…'
                : `${filteredResults.length} source-backed receipt${filteredResults.length !== 1 ? 's' : ''}`}
            </span>
            {!isSearching && displayedSources.length > 0 && (
              <span className="font-mono text-xs">Sources: {displayedSources.join(' • ')}</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
